import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth.js';

/**
 * @graph-node {Page}
 * @graph-stack line-user-id-resolver
 * @graph-domain Infrastructure
 * @graph-business アクセス拒否ページ。admin権限のないユーザーに対しロックアイコンとログアウトボタンを表示
 * @graph-connects useAuth [calls] 認証フック呼び出し（ログアウト用）
 */
export const Route = createFileRoute('/forbidden')({
  component: ForbiddenPage,
});

/** @graph-connects none */
function ForbiddenPage() {
  const { t } = useTranslation();
  const { logout, isLoggingOut } = useAuth();

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center p-4">
      {/* 背景装飾 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="gradient-orb gradient-orb-primary w-96 h-96 -top-48 -right-48 opacity-10" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="glass-card p-8 text-center animate-fade-in-up">
          {/* ロックアイコン */}
          <div className="mx-auto w-16 h-16 rounded-full bg-[var(--color-error-muted)] flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 text-[var(--color-error)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>

          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
            {t('auth.forbidden')}
          </h1>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            {t('auth.forbiddenMessage')}
          </p>

          {/* ログアウトボタン */}
          <button
            onClick={() => {
              logout();
            }}
            disabled={isLoggingOut}
            className="mt-6 px-6 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)] transition-colors disabled:opacity-50"
            aria-label={t('auth.logout')}
          >
            {isLoggingOut ? (
              <span className="flex items-center gap-2">
                <div className="loading-spinner loading-spinner-sm" />
                {t('auth.logout')}
              </span>
            ) : (
              t('auth.logout')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
